import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { siteConfig } from '@/mocks/siteConfig';
import { categories } from '@/mocks/categories';
import { getComicById, getRelatedComics } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mb-6">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
          <p className="text-foreground-500 mb-6">该漫画可能已下架或链接不正确</p>
          <Link
            to="/category"
            className="bg-primary-500 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            浏览其他漫画
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedComics = getRelatedComics(comic, 6);
  const comicCats = categories.filter((c) => comic.categories.includes(c.id));
  const primaryCategory = comicCats[0];

  const pageTitle = `${comic.title} - 韩漫在线阅读|韩漫阁`;
  const pageDescription = `${comic.title}，${comic.author}作品。${comic.description.slice(0, 80)}。韩漫阁提供免费无遮下拉式韩漫在线阅读，高清画质流畅体验。`;
  const pageKeywords = `${comic.title},韩漫在线阅读,韩国漫画,免费漫画,无遮漫画,${comicCats.map(c => c.name).join(',')}`;
  const canonicalPath = `/article/${comic.id}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.updatedAt,
    dateModified: comic.updatedAt,
    image: comic.coverImage,
    url: `${siteConfig.url}${canonicalPath}`,
    publisher: { '@type': 'Organization', name: siteConfig.name, url: siteConfig.url },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteConfig.url}${canonicalPath}` },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonicalPath={canonicalPath}
        ogType="article"
        ogImage={comic.coverImage}
        schemaJson={{ ...organizationSchema, ...articleSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '分类浏览', href: '/category' },
            ...(primaryCategory ? [{ label: primaryCategory.name, href: `/category?cat=${primaryCategory.slug}` }] : []),
            { label: comic.title },
          ]}
        />

        {/* Article Header */}
        <article className="w-full max-w-6xl mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col md:flex-row gap-10">
            {/* Left: Cover */}
            <div className="md:w-[300px] flex-shrink-0">
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-secondary-100 sticky top-24">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  title={`${comic.title} - 韩漫在线阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Right: Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                {comic.status === '连载中' ? (
                  <span className="bg-primary-100 text-primary-700 text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap">
                    ● {comic.status}
                  </span>
                ) : (
                  <span className="bg-accent-100 text-accent-700 text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap">
                    ✓ {comic.status}
                  </span>
                )}
                {comic.newRelease && (
                  <span className="bg-red-100 text-red-600 text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap">NEW</span>
                )}
                <span className="text-xs text-foreground-500">共 {comic.chapterCount} 话</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-primary-500"></i>
                  <span className="text-sm font-semibold text-foreground-950">{comic.rating}</span>
                  <span className="text-xs text-foreground-500">分</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-eye-line text-foreground-400"></i>
                  <span className="text-xs text-foreground-500">{(comic.views / 10000).toFixed(0)}万次阅读</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-time-line text-foreground-400"></i>
                  <span className="text-xs text-foreground-500">更新于 {comic.updatedAt}</span>
                </div>
              </div>

              {/* categories */}
              <div className="flex flex-wrap items-center gap-2 mb-6">
                {comicCats.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category?cat=${cat.slug}`}
                    className="bg-secondary-100 text-secondary-700 text-xs px-3 py-1 rounded-full hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {/* tags */}
              <div className="flex flex-wrap items-center gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span key={tag} className="bg-background-100 text-foreground-500 text-xs px-2.5 py-1 rounded whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>

              {/* description */}
              <p className="text-sm text-foreground-600 leading-relaxed mb-8">{comic.description}</p>

              {/* Read Button */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    document.getElementById('read-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="bg-primary-500 text-background-50 px-8 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-center"
                >
                  开始阅读（下拉式）
                  <i className="ri-arrow-down-line ml-2"></i>
                </button>
                <button className="border border-background-300 text-foreground-700 px-8 py-3 rounded-full text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap text-center">
                  <i className="ri-bookmark-line mr-2"></i>加入收藏
                </button>
              </div>
            </div>
          </div>
        </article>

        {/* Reading Section */}
        <section id="read-section" className="bg-foreground-950 py-16 md:py-20">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">下拉式阅读体验</h2>
            <p className="text-foreground-400 text-sm md:text-base mb-8">
              点击下方按钮开始阅读，享受流畅的下拉式漫画体验
            </p>
            <div className="flex flex-col items-center gap-4">
              <Link
                to={`/article/${comic.id}`}
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-10 py-3.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-fill text-lg"></i>
                从第1话开始阅读
              </Link>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-lg mt-4">
                {[1, 2, 3, 4].map((ch) => (
                  <Link
                    key={ch}
                    to={`/article/${comic.id}`}
                    className="block bg-foreground-800 text-background-50/70 text-xs py-2.5 rounded-lg hover:bg-foreground-700 hover:text-background-50 transition-colors cursor-pointer text-center whitespace-nowrap"
                  >
                    第{ch}话
                  </Link>
                ))}
              </div>
              <p className="text-foreground-500 text-xs mt-4">共 {comic.chapterCount} 话 · 持续更新中</p>
            </div>
          </div>
        </section>

        {/* Related */}
        {relatedComics.length > 0 && (
          <section className="py-16 md:py-20 bg-background-50">
            <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">相关推荐</h2>
                <Link to="/category" className="text-primary-500 text-sm font-medium hover:text-primary-600 transition-colors whitespace-nowrap">
                  更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="flex gap-5 overflow-x-auto pb-4">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} variant="compact" />
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}